import { useState } from 'react'
import { Check, ExternalLink, ShieldCheck } from 'lucide-react'
import {
  PickerMenu,
  PickerMenuContent,
  PickerMenuItem,
  PickerMenuList,
  PickerMenuTrigger,
} from '@/components/picker-menu'
import { formatMoney } from '@/lib/utils'
import type { QuoteLine } from '../api/types'
import { coverageText, type EquipmentProtection, type PlanActions, type ProtectionPlanOffer } from './protection-plans-api'

function OfferItem({ offer, chosen, onSelect }: { offer: ProtectionPlanOffer; chosen: boolean; onSelect: () => void }) {
  return (
    <PickerMenuItem
      className="grid grid-cols-[16px_minmax(0,1fr)_auto] items-center gap-x-2"
      onSelect={onSelect}
      aria-current={chosen || undefined}
    >
      {chosen ? <Check aria-hidden="true" className="size-3.5" /> : <span />}
      <span className="min-w-0">
        <span className="block font-medium">+{coverageText(offer.months_added)}</span>
        <span className="block text-[11px] text-muted-foreground">{coverageText(offer.term_months)} total</span>
      </span>
      <span className="font-semibold tabular-nums">{formatMoney(Number(offer.price))}</span>
    </PickerMenuItem>
  )
}

/** The equipment row's Safeware plans (USA-1162): one click opens them, a second adds or switches the plan. */
export function EquipmentPlanMenu({
  line,
  protection,
  plans,
  actions,
}: {
  line: QuoteLine
  protection: EquipmentProtection | undefined
  /** The plan lines already under this equipment. */
  plans: QuoteLine[]
  actions: PlanActions
}) {
  const [open, setOpen] = useState(false)
  if (!protection?.offers.length) return null
  const name = line.model || line.description
  const current = plans[0]
  return (
    <PickerMenu open={open} onOpenChange={setOpen}>
      <PickerMenuTrigger asChild>
        <button
          aria-label={`Protection plans for ${name}`}
          className="inline-flex min-h-7 items-center gap-1 rounded-sm text-[11px] font-medium underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
          type="button"
        >
          <ShieldCheck aria-hidden="true" className="size-3.5" />
          {current ? 'Change plan' : 'Protection plan'}
        </button>
      </PickerMenuTrigger>
      <PickerMenuContent
        title={
          protection.warranty_assumed
            ? 'Safeware plans'
            : `Safeware plans · ${coverageText(protection.manufacturer_warranty_months)} manufacturer warranty`
        }
        align="start"
        className="w-64"
      >
        <PickerMenuList className="pt-0">
          {protection.offers.map((offer) => (
            <OfferItem
              chosen={current?.product_id === offer.product_id}
              key={offer.product_id}
              offer={offer}
              onSelect={() => {
                setOpen(false)
                if (current?.product_id !== offer.product_id) actions.onChoosePlan(line, offer, current)
              }}
            />
          ))}
        </PickerMenuList>
      </PickerMenuContent>
    </PickerMenu>
  )
}

/** A plan line's coverage and terms link. */
export function PlanLineDetails({ line }: { line: QuoteLine }) {
  const plan = line.protection_plan
  if (!plan) return null
  return (
    <p className="mt-1 flex flex-wrap items-center gap-x-2 text-[11px] text-muted-foreground">
      <span>
        <span className="font-semibold text-foreground">+{coverageText(plan.months_added)}</span> ·{' '}
        {coverageText(plan.term_months)} total
      </span>
      {plan.terms_url ? (
        // eslint-disable-next-line no-restricted-syntax -- Safeware's site
        <a
          className="inline-flex min-h-7 items-center gap-0.5 font-medium text-foreground underline-offset-2 hover:underline"
          href={plan.terms_url}
          rel="noopener noreferrer"
          target="_blank"
        >
          Plan terms
          <ExternalLink aria-hidden="true" className="size-3" />
        </a>
      ) : null}
    </p>
  )
}
